import {
    ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output,
} from '@angular/core';

import { isNoindexCategory } from '@core/application/use-cases/seo/is-no-index-category.use-case';
import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import { getFreeTextAttachment } from '@features/category/application/services/category.utils';
import { MenuCategory, freeTextAttachments } from '@shared/header/models/category.model';
import { ActiveCategoriesTree } from '@shared/menu-categories/store/menu-categories.state';

@Component({
    selector: 'li [header-menu-category]',
    templateUrl: './header-menu-category.component.html',
    styleUrls: ['./header-menu-category.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderMenuCategoryComponent implements OnInit {
    @Input() public indexItem: number;

    @Input() public category: MenuCategory;

    @Input() public hasChild = false;

    @Input() public activeCategories: Record<number, ActiveCategoriesTree>;

    @Input() public sectionIndex: number;

    @Input() public sectionName: string;

    @Input() public isRtl: boolean;

    @Input() public level = 1;

    @Input() public debugShowCategoryId: boolean;

    @Output() public readonly sendOpenCategory = new EventEmitter();

    @Output() public readonly sendOpenSubCategory = new EventEmitter();

    public freeTextAttachments: freeTextAttachments[];

    public isLight;

    public isNoindex: boolean;

    public isExpandedScope: 'open' | 'close' | null = null;

    private readonly storageService = inject(CoreStorageService);

    public toggleCategory($event: MouseEvent, category: MenuCategory, index: number, isActive: boolean) {
        this.preventDefault($event);

        if (this.category.canExpandableScope) {
            this.isExpandedScope = this.isExpandedScope === 'open' ? 'close' : 'open';

            let categoriesInactiveSession: any = this.storageService.getValue('categoriesInactiveSession', 'SESSION') ?? [];
            categoriesInactiveSession = this.isExpandedScope === 'open'
                ? categoriesInactiveSession.filter(id => id !== category.id)
                : [...categoriesInactiveSession, category.id];
            this.storageService.setValue('categoriesInactiveSession', JSON.stringify(categoriesInactiveSession), 'SESSION', 'FUNCTIONAL_PERSONALIZATION');
        } else {
            this.sendOpenCategory.emit({
                category, index, show: category.subcategories?.length > 0 ? !isActive : true,
            });
        }
    }

    public preventDefault($event: MouseEvent) {
        $event.preventDefault();
        $event.stopPropagation();
    }

    public openSubCategory(category: MenuCategory) {
        this.sendOpenSubCategory.emit({
            category,
        });
    }

    public ngOnInit(): void {
        this.freeTextAttachments = getFreeTextAttachment(this.category);
        const chlds = this.category.subcategories?.filter(cat => !cat.isMenuHidden).length;
        this.hasChild = chlds > 0;
        this.isLight = !this.hexIsLight(
            this.category?.backgroundColor !== 'inherit' && !!this.category?.backgroundColor
                ? this.category?.backgroundColor : '#ffffff'
        );
        this.isNoindex = isNoindexCategory(this.category.attachments);

        if (this.category.canExpandableScope) {
            this.isExpandedScope = this.category.canExpandableScope ? 'open' : 'close';
        }

        const categoriesInactiveSession: any = this.storageService.getValue('categoriesInactiveSession', 'SESSION') || [];
        if (categoriesInactiveSession.includes(this.category.id)) {
            this.isExpandedScope = 'close';
        }
    }

    private hexIsLight(color) {
        const hex = color.replace('#', '');
        const cR = Number.parseInt(hex.substr(0, 2), 16);
        const cG = Number.parseInt(hex.substr(2, 2), 16);
        const cB = Number.parseInt(hex.substr(4, 2), 16);
        const brightness = (cR * 299 + cG * 587 + cB * 114) / 1000;

        return brightness > 155;
    }
}
